"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { CheckCircle2, Zap, Sparkles, ArrowRight } from "lucide-react";

const starterFeatures = [
  "AI Email Booking Parsing (BookMyGarage, ClickMechanic, Fixter)",
  "1 Staff / Technician Account",
  "Up to 100 Vehicles in CRM",
  "Live DVSA MOT & Spec Lookups",
  "Google Calendar 2-Way Sync",
  "100 Reminder SMS / Email Credits / mo",
  "Digital Job Cards & Invoicing",
  "Standard Email Support",
];

const professionalFeatures = [
  "Everything in Starter, plus:",
  "Up to 5 Staff & Technician Accounts",
  "Up to 500 Vehicles in CRM",
  "Unlimited DVSA Lookups & History",
  "250 Reminder SMS / Email Credits / mo",
  "MOT Advisory Monetization & Estimator",
  "Smart Cancellation Auto-Slot Release",
  "Visual Email Template Builder & PDF Rules",
  "Real-Time Revenue Analytics & ROI Tracking",
  "Priority Email & Chat Support",
];

const businessFeatures = [
  "Everything in Professional, plus:",
  "Unlimited Staff & Technicians",
  "Unlimited Vehicles & Customers",
  "600 Reminder SMS / Email Credits / mo",
  "Multi-Location Branch Switcher",
  "Custom Automation Rules & Triggers",
  "Role-Based Permissions & Staff Access",
  "Priority 24/7 Phone & Email Support",
];

export default function PricingCardsWrapper() {
  const searchParams = useSearchParams();
  const [refParam, setRefParam] = useState("");

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setRefParam(`&ref=${ref}`);
      // Store in localStorage for persistence
      localStorage.setItem("referral_code", ref);
      localStorage.setItem("referral_code_timestamp", Date.now().toString());
    }
  }, [searchParams]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch max-w-7xl mx-auto">
      
      {/* 1. Starter Plan */}
      <div className="bg-white border-2 border-slate-200/90 rounded-3xl p-7 md:p-8 shadow-sm hover:shadow-xl hover:border-blue-300 hover:-translate-y-1 transition-all flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 uppercase tracking-wider">
              Solo / Mobile
            </span>
          </div>

          <h3 className="text-2xl font-black text-slate-900">Starter</h3>
          <p className="text-xs sm:text-sm text-slate-600 mt-1 min-h-[40px]">
            Ideal for mobile mechanics and independent workshops starting out.
          </p>

          <div className="my-6 pb-6 border-b border-slate-100 flex items-baseline gap-1">
            <span className="text-4xl sm:text-5xl font-black text-slate-900">£29</span>
            <span className="text-sm font-semibold text-slate-500">/ month + VAT</span>
          </div>

          <p className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-4">
            Included Features:
          </p>

          <ul className="space-y-3 mb-8">
            {starterFeatures.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="text-blue-600 shrink-0 mt-0.5" size={16} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="pt-4">
          <Link
            href={`/signup?plan=starter${refParam}`}
            className="w-full border-2 border-slate-300 hover:border-blue-600 text-slate-900 hover:text-blue-600 hover:bg-blue-50/50 font-bold py-3.5 px-6 rounded-xl text-center transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            Start 14-Day Free Trial
          </Link>
          <p className="text-[11px] text-center text-slate-400 mt-2.5">No credit card required</p>
        </div>
      </div>

      {/* 2. Professional Plan (Featured Highlight) */}
      <div className="bg-linear-to-b from-slate-900 to-slate-950 text-white border-2 border-blue-500 rounded-3xl p-7 md:p-8 shadow-2xl shadow-blue-500/20 relative lg:-translate-y-2 flex flex-col justify-between">
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-linear-to-r from-blue-500 to-teal-400 text-slate-950 px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest shadow-md flex items-center gap-1.5">
          <Sparkles size={13} /> Most Popular
        </div>

        <div>
          <div className="flex items-center justify-between mb-4 mt-2">
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30 uppercase tracking-wider">
              Single MOT Garage
            </span>
          </div>

          <h3 className="text-2xl font-black text-white flex items-center gap-2">
            Professional <Zap size={20} className="text-teal-400 fill-teal-400" />
          </h3>
          <p className="text-xs sm:text-sm text-slate-300 mt-1 min-h-[40px]">
            The complete automated engine for busy MOT stations and service centers.
          </p>

          <div className="my-6 pb-6 border-b border-slate-800 flex items-baseline gap-1">
            <span className="text-4xl sm:text-5xl font-black text-white">£59</span>
            <span className="text-sm font-semibold text-slate-400">/ month + VAT</span>
          </div>

          <p className="text-xs font-bold text-teal-400 uppercase tracking-wider mb-4">
            Everything to Scale:
          </p>

          <ul className="space-y-3 mb-8">
            {professionalFeatures.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-200">
                <CheckCircle2 className="text-teal-400 shrink-0 mt-0.5" size={16} />
                <span className={idx === 0 ? "font-bold text-white" : ""}>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="pt-4">
          <Link
            href={`/signup?plan=professional${refParam}`}
            className="w-full bg-linear-to-r from-blue-600 to-teal-500 hover:from-blue-500 hover:to-teal-400 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.02] text-center transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            Start 14-Day Free Trial <ArrowRight size={18} />
          </Link>
          <p className="text-[11px] text-center text-slate-400 mt-2.5">Includes full AI automation features</p>
        </div>
      </div>

      {/* 3. Business Plan */}
      <div className="bg-white border-2 border-slate-200/90 rounded-3xl p-7 md:p-8 shadow-sm hover:shadow-xl hover:border-blue-300 hover:-translate-y-1 transition-all flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200 uppercase tracking-wider">
              Multi-Branch / Enterprise
            </span>
          </div>

          <h3 className="text-2xl font-black text-slate-900">Business</h3>
          <p className="text-xs sm:text-sm text-slate-600 mt-1 min-h-[40px]">
            For established workshops and multi-location automotive franchises.
          </p>

          <div className="my-6 pb-6 border-b border-slate-100 flex items-baseline gap-1">
            <span className="text-4xl sm:text-5xl font-black text-slate-900">£99</span>
            <span className="text-sm font-semibold text-slate-500">/ month + VAT</span>
          </div>

          <p className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-4">
            Maximum Power & Capacity:
          </p>

          <ul className="space-y-3 mb-8">
            {businessFeatures.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="text-teal-600 shrink-0 mt-0.5" size={16} />
                <span className={idx === 0 ? "font-bold text-slate-900" : ""}>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="pt-4">
          <Link
            href={`/signup?plan=business${refParam}`}
            className="w-full border-2 border-slate-300 hover:border-teal-600 text-slate-900 hover:text-teal-700 hover:bg-teal-50/50 font-bold py-3.5 px-6 rounded-xl text-center transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            Start 14-Day Free Trial
          </Link>
          <p className="text-[11px] text-center text-slate-400 mt-2.5">No setup fees • Instant access</p>
        </div>
      </div>

    </div>
  );
}
